/**
 * SSE Signal Service
 * Manages EventSource connection to /sse/signals for real-time RF signal feed.
 */

import { withSseAccessToken } from '@signals/app-shell/utils/sseAuthUrl';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export interface SignalEvent {
  signal_type: 'wifi' | 'bluetooth' | 'cellular';
  // Common fields
  device_id: string;
  latitude: string | null;
  longitude: string | null;
  timestamp: string;
  rssi: number | string | null;
  device_type: string;
  device_type_icon: string;
  badges: string[];
  // WiFi-specific
  bssid?: string;
  ssid?: string;
  frequency?: number;
  channel?: number;
  security?: string;
  standard?: string;
  is_passpoint?: boolean;
  operator_friendly_name?: string | null;
  venue_name?: string | null;
  is_80211mc_responder?: boolean;
  information_elements?: string;
  // BLE-specific
  address?: string;
  name?: string;
  bt_type?: string;
  manufacturer_id?: number | null;
  manufacturer_data?: string | null;
  service_uuids?: string | null;
  is_connectable?: boolean | null;
  tx_power_level?: number | null;
  is_randomized?: boolean;
  estimated_distance_m?: number | null;
  // Cellular-specific
  cell_tower_id?: string;
  operator?: string;
  operator_name?: string;
  network_type?: string;
  mcc?: string;
  mnc?: string;
}

type SignalListener = (event: SignalEvent) => void;

class SSESignalService {
  private eventSource: EventSource | null = null;
  private listeners: Set<SignalListener> = new Set();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 2000;
  private maxReconnectDelay = 30000;
  private connected = false;

  connect(signalType?: string, deviceId?: string): void {
    this.disconnect();

    const params = new URLSearchParams();
    if (signalType) params.set('signal_type', signalType);
    if (deviceId) params.set('device_id', deviceId);

    const qs = params.toString();
    const url = withSseAccessToken(`${API_BASE}/sse/signals${qs ? `?${qs}` : ''}`);

    try {
      this.eventSource = new EventSource(url);

      this.eventSource.addEventListener('connected', () => {
        this.connected = true;
        this.reconnectDelay = 2000;
      });

      const handleSignal = (e: MessageEvent) => {
        try {
          const data: SignalEvent = JSON.parse(e.data);
          this.listeners.forEach(fn => fn(data));
        } catch {
          // ignore parse errors
        }
      };

      this.eventSource.addEventListener('wifi_signal', handleSignal);
      this.eventSource.addEventListener('bluetooth_signal', handleSignal);
      this.eventSource.addEventListener('cellular_signal', handleSignal);

      this.eventSource.onerror = () => {
        this.connected = false;
        this.eventSource?.close();
        this.eventSource = null;
        this.scheduleReconnect(signalType, deviceId);
      };
    } catch (err) {
      this.scheduleReconnect(signalType, deviceId);
    }
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.connected = false;
  }

  subscribe(listener: SignalListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  isConnected(): boolean {
    return this.connected;
  }

  private scheduleReconnect(signalType?: string, deviceId?: string): void {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, this.maxReconnectDelay);
      this.connect(signalType, deviceId);
    }, this.reconnectDelay);
  }
}

export const sseSignalService = new SSESignalService();
